import api from 'src/config/api';
import { getJsonResponse } from 'src/utils/ApiUtils';
import InterventionOrder, {
  InterventionOrderCriteria,
  PostInterventionOrderBody,
} from 'src/interfaces/api/InterventionOrder';
import { CollectionResponse } from '../../interfaces/api/api';
import { Order } from '../../interfaces/Table';
import useAuth from './useAuth';

const useInterventionOrder = () => {
  const { fetch: authFetch } = useAuth();

  const create = async (
    interventionOrder: PostInterventionOrderBody & { token?: string },
    fromProvider = false
  ): Promise<InterventionOrder> => {
    const { endpoint, ...options } = api.interventionOrder.create(interventionOrder, fromProvider);

    const currentFetch = fromProvider ? fetch : authFetch;

    const response = await currentFetch(endpoint, options);

    try {
      return await getJsonResponse<InterventionOrder>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const update = async (id: string, interventionOrder: PostInterventionOrderBody): Promise<InterventionOrder> => {
    const { endpoint, ...options } = api.interventionOrder.update(id, interventionOrder);

    const response = await authFetch(endpoint, options);

    try {
      return await getJsonResponse<InterventionOrder>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const get = async (id: InterventionOrder['id']): Promise<InterventionOrder> => {
    const { endpoint, ...options } = api.interventionOrder.get(id);

    const response = await authFetch(endpoint, options);

    try {
      return await getJsonResponse<InterventionOrder>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const invoice = async (id: InterventionOrder['id']): Promise<InterventionOrder> => {
    const { endpoint, ...options } = api.interventionOrder.invoice(id);

    const response = await authFetch(endpoint, options);

    try {
      return await getJsonResponse<InterventionOrder>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getAll = async (
    criteria: InterventionOrderCriteria,
    page: number,
    limit: number,
    orderBy: keyof InterventionOrder = 'createdAt',
    order: Order
  ): Promise<CollectionResponse<InterventionOrder>> => {
    const { endpoint, ...options } = api.interventionOrder.getAll(criteria, orderBy, order, page, limit);

    const response = await authFetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<InterventionOrder>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  return { create, update, getAll, get, invoice };
};

export default useInterventionOrder;
